import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RespiratoryRate from "../../icons/RespiratoryRate";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../../utils/results/spirometry";
import MetricValue from "../MetricValue";
import { SignalChart } from "../../charts/SignalChart";
import FlowLoopChart from "../../charts/FlowLoopChart";
import { trackEvent } from "../../../utils/analytics/track";
import { useUserWebAppPresentationFeatures } from "../../../hooks/useUserWebAppPresentationFeatures";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../../utils/results/recordingIdTime";
import LungIcon from "../../icons/LungIcon";
import Spacer from "../../Spacer";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import ResultViewScaffold from "../ResultViewScaffold";
import ResultCard from "../../common/ResultCard";
import AveragedFlowLoopsSection from "../AveragedFlowLoopsSection";
import RecordingQualitySection from "../RecordingQualitySection";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../../utils/results/resultModePresentation";
import {
  RECORDING_QUALITY_CHECK_STATES,
  RECORDING_QUALITY_OVERALL_STATES,
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  summarizeRecordingQualityChecks,
} from "../../../utils/results/recordingQuality";
import {
  buildSpirometryAdditionalMetricsFields,
  buildSpirometryAdditionalMetricsRecord,
  getSpirometryQualityHintKeys,
  isSpirometryResultReady,
} from "../../../utils/results/spirometryView";
import { getByPath } from "../../../utils/results/metricsKVTableHelpers";
import {
  pickAveragedFlowLoop,
  pickPrimaryFlowLoopRecord,
} from "../../../utils/results/flowLoops";
import {
  getPointSeriesMaxSecs,
  getTimeSeriesMaxSecs,
  normaliseTimeValuePoints,
} from "../../../utils/results/viewTimeSeries";

function SpirometryResultViewImpl({
  data,
  docId,
  variant,
  repeatLinkTo = "",
  footer = null,
  predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
}) {
  const { t, i18n } = useTranslation();
  const { resultFeatures = {}, elementFeatures = {} } =
    useUserWebAppPresentationFeatures();
  const showSignalQuality = Boolean(resultFeatures.signalQuality);
  const showSpirometryQuality = Boolean(resultFeatures.spirometryQuality);
  const showFev1 = Boolean(resultFeatures.fev1);
  const showFvc = Boolean(resultFeatures.fvc);
  const showFev1Fvc = Boolean(resultFeatures.fev1Fvc);
  const showPef = Boolean(resultFeatures.pef);
  const showFlowLoops = Boolean(resultFeatures.flowLoops);
  const showRr = Boolean(resultFeatures.rr);
  const showRespiratorySignalChart = Boolean(
    elementFeatures.respiratorySignalChart,
  );
  const showSpirometrySegmentChart = Boolean(
    elementFeatures.spirometrySegmentChart,
  );
  const showSpirometryMetrics = showFev1 || showFvc || showFev1Fvc || showPef;
  const showQualitySummary = showSignalQuality || showSpirometryQuality;
  const showTraceDetails =
    showRespiratorySignalChart || showSpirometrySegmentChart || showFlowLoops;

  const {
    fev1,
    fvc,
    fev1FvcRatio,
    pef,
    rr,
    signalQuality,
    spirometryQuality,
    spirometryQualityChecks,
    zeroedMetrics,
    additionalMetrics,
    processedSignal,
  } = useMemo(
    () => pickSpirometryResult(data, { predictionSource }),
    [data, predictionSource],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );

  const resultQualityHints = useMemo(
    () =>
      getSpirometryQualityHintKeys(spirometryQualityChecks).map((checkKey) =>
        t(`result.spiro.qualityHints.items.${checkKey}`),
      ),
    [spirometryQualityChecks, t],
  );

  const recordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(docId),
    [docId],
  );
  const recordingQualitySummary = useMemo(
    () =>
      summarizeRecordingQualityChecks([
        showSignalQuality
          ? buildRecordingQualityCheck({
              key: "signal",
              label: t("result.spiro.signalQuality", "Signal quality"),
              rawStatus: signalQuality,
              recordingTimestamp,
              message:
                signalQuality != null
                  ? t("result.spiro.qualityFailTitle", "Low signal quality.")
                  : null,
            })
          : null,
        showSpirometryQuality
          ? buildRecordingQualityCheck({
              key: "spirometry",
              label: t(
                "result.spiro.spirometryQuality",
                "Spirometry quality",
              ),
              rawStatus: spirometryQuality,
              state:
                zeroedMetrics &&
                signalQuality !== "fail" &&
                spirometryQuality !== "fail"
                  ? RECORDING_QUALITY_CHECK_STATES.FAIL
                  : null,
              recordingTimestamp,
              message:
                zeroedMetrics &&
                signalQuality !== "fail" &&
                spirometryQuality !== "fail"
                  ? t(
                      "result.spiro.overallQualityFailTitle",
                      "Recording quality needs attention.",
                    )
                  : spirometryQuality != null
                    ? t(
                        "result.spiro.manoeuvreQualityFailTitle",
                        "Spirometry manoeuvre quality needs attention.",
                      )
                    : null,
            })
          : null,
      ]),
    [
      recordingTimestamp,
      showSignalQuality,
      showSpirometryQuality,
      signalQuality,
      spirometryQuality,
      t,
      zeroedMetrics,
    ],
  );
  const resultQualityIsDisabled =
    recordingQualitySummary.overallState !==
    RECORDING_QUALITY_OVERALL_STATES.PASSED;
  const compactRecordingQualityStatus = useMemo(() => {
    if (!showQualitySummary) {
      return null;
    }

    if (
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PASSED &&
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PENDING
    ) {
      return null;
    }

    const summaryCopy = getRecordingQualitySummaryCopy(t, recordingQualitySummary);
    return summaryCopy
      ? {
          tone: summaryCopy.tone,
          text: summaryCopy.title,
          accessibleLabel: summaryCopy.accessibleLabel,
        }
      : null;
  }, [recordingQualitySummary, showQualitySummary, t]);
  const showRecordingQualitySection =
    showQualitySummary && compactRecordingQualityStatus == null;

  const rrValue = useMemo(() => {
    if (typeof rr?.average === "number" && Number.isFinite(rr.average)) {
      return rr.average;
    }
    if (typeof rr?.mean === "number" && Number.isFinite(rr.mean)) {
      return rr.mean;
    }
    return null;
  }, [rr]);

  const detrendedSignalPoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.detrendedSignalPoints || [],
    );
  }, [processedSignal]);

  const detrendedSignalMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(
      processedSignal?.timeSeconds,
      getPointSeriesMaxSecs(detrendedSignalPoints, 1),
    );
  }, [processedSignal, detrendedSignalPoints]);

  const spirometryMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(processedSignal?.spirometryTimeSeconds, 60);
  }, [processedSignal]);

  const normalisedSignalTracePoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.spirometrySignalTracePoints || [],
    );
  }, [processedSignal]);
  const flowLoopRecord = useMemo(() => pickPrimaryFlowLoopRecord(data), [data]);
  const averagedFlowLoop = useMemo(() => pickAveragedFlowLoop(data), [data]);

  const additionalMetricsRecord = useMemo(
    () =>
      buildSpirometryAdditionalMetricsRecord(
        additionalMetrics,
        rr,
        signalQuality,
        spirometryQuality,
      ),
    [additionalMetrics, rr, signalQuality, spirometryQuality],
  );

  const additionalMetricsFields = useMemo(
    () => buildSpirometryAdditionalMetricsFields(t, resultFeatures),
    [resultFeatures, t],
  );

  // const mpmDetrendedSignal = useMemo(
  //   () => pickCardiopulmonaryDetrendedSignal(data),
  //   [data],
  // );

  const details = showTraceDetails ? (
    <section className="w-full p-4 pt-2 flex flex-col gap-4">
      {showRespiratorySignalChart ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t("result.spiro.signalFullDetrended", "Signal")}
        >
          <SignalChart
            smoothSignal={detrendedSignalPoints}
            rawSignal={[]}
            peaks={[]}
            troughs={[]}
            maxSecs={detrendedSignalMaxSecs}
            height={196}
          />
        </ResultCard>
      ) : null}

      {showSpirometrySegmentChart ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t("result.spiro.signalSpiroSegment", "Spirometry Segment")}
        >
          <SignalChart
            smoothSignal={normalisedSignalTracePoints}
            rawSignal={[]}
            peaks={[]}
            troughs={[]}
            maxSecs={spirometryMaxSecs}
            height={220}
            tickStepSize={0.5}
          />
        </ResultCard>
      ) : null}

      {showFlowLoops ? (
        <>
          <ResultCard
            icon={getResultModeIcon(variant)}
            title={t("result.spiro.spiroFlowLoop", "Spirometry Flow Loop")}
          >
            <FlowLoopChart
              title={null}
              points={processedSignal?.spirometryFlowLoop}
              xTitle={t("result.spiro.volumeProxy", "Volume")}
              yTitle={t("result.spiro.flowProxy", "Flow")}
              height={280}
            />
          </ResultCard>

          {averagedFlowLoop ? (
            <ResultCard
              icon={getResultModeIcon(variant)}
              title={t(
                averagedFlowLoop.mode === "windowed"
                  ? "results.flowLoops.averagedSectionTitlePlural"
                  : "results.flowLoops.averagedSectionTitle",
                averagedFlowLoop.mode === "windowed"
                  ? "Average Flow-Volume Loops"
                  : "Average Flow-Volume Loop",
              )}
            >
              <AveragedFlowLoopsSection flowLoopSource={flowLoopRecord} />
            </ResultCard>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {additionalMetricsFields.map((field) => {
              const value = field.fmt(
                getByPath(additionalMetricsRecord, field.path),
                additionalMetricsRecord,
              );

              return (
                <ResultCard
                  key={field.path}
                  icon={getResultModeIcon(variant)}
                  title={t(field.i18nKey, { defaultValue: field.label })}
                >
                  <p className="title-text text-primary">
                    {value}
                    {field.unit && value !== "--" ? (
                      <span className="muted-text"> {field.unit}</span>
                    ) : null}
                  </p>
                </ResultCard>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={getResultModeLabel(variant, t)}
      icon={getResultModeIcon(variant)}
      recordingTime={recordingTime}
      statusLine={compactRecordingQualityStatus}
      details={details}
      footer={footer}
    >
      <Spacer />
      {showRecordingQualitySection ? (
        <RecordingQualitySection
          summary={recordingQualitySummary}
          hints={resultQualityHints}
          hintsTitle={t("result.spiro.qualityHints.title", "What to improve")}
          guidanceLinkTo="/guidance/spirometry"
          guidanceCtaLabel={t("result.spiro.guidanceCta", "Tips")}
          repeatLinkTo={repeatLinkTo}
          repeatCtaLabel={t(
            "result.recordingQuality.repeatCta",
            "Repeat",
          )}
          onGuidanceClick={() =>
            trackEvent("spirometry_result_click_guidance", {
              signal_quality: signalQuality,
              spirometry_quality: spirometryQuality,
              zeroed_metrics: zeroedMetrics,
            })
          }
        />
      ) : null}
      {showSpirometryMetrics ? (
        <ResultSummarySection
          icon={<RespiratoryRate />}
          title={t("result.spiro.spirometryTitle", "Spirometry")}
          disabled={resultQualityIsDisabled}
        >
          {showFev1 ? (
            <ResultSummaryRow
              label={t("result.spiro.fev1", "FEV1")}
              value={
                <MetricValue
                  metric="fev1"
                  value={fev1}
                  unit={t("result.spiro.FEV1Unit", "L")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showFvc ? (
            <ResultSummaryRow
              label={t("result.spiro.fvc", "FVC")}
              value={
                <MetricValue
                  metric="fvc"
                  value={fvc}
                  unit={t("result.spiro.FVCUnit", "L")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showFev1Fvc ? (
            <ResultSummaryRow
              label={t("result.spiro.fev1FvcRatio", "FEV1/FVC")}
              value={
                <MetricValue
                  metric="fev1FvcRatio"
                  value={fev1FvcRatio}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showPef ? (
            <ResultSummaryRow
              label={t("result.spiro.pef", "PEF")}
              value={
                <MetricValue
                  metric="pef"
                  value={pef}
                  unit={t("result.spiro.PEFUnit", "L/min")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
        </ResultSummarySection>
      ) : null}

      {showRr ? (
        <ResultSummarySection
          icon={<LungIcon />}
          title={t("result.spiro.respiratory", "Respiratory")}
          disabled={resultQualityIsDisabled}
        >
          <ResultSummaryRow
            label={t("result.resp.rrLongLabel", "Respiratory Rate")}
            value={
              <MetricValue
                metric="rr"
                value={rrValue}
                unit={t("result.resp.rrUnit", "bpm")}
                recordingTimestamp={recordingTimestamp}
              />
            }
            disabled={resultQualityIsDisabled}
          />
        </ResultSummarySection>
      ) : null}
    </ResultViewScaffold>
  );
}

const SpirometryResultView = memo(SpirometryResultViewImpl);

SpirometryResultView.isReady = isSpirometryResultReady;

export default SpirometryResultView;
